import React, { FC, memo } from 'react';
import { pluralize } from 'utils/pluralize';
import SectionHeading from '../SectionHeading';
import {
  ButtonArrow,
  CommunityButton,
  ConnectionBox,
  ConnectionLabel,
  ConnectionNumber,
  ConnectionsRow,
  Container,
  Description,
  EmptyBody,
  EmptyCard,
  EmptyTitle,
  MemberAvatar,
  NetworkInfo,
  NetworkItem,
  NetworkLink,
  NetworkList,
  NetworkName,
  NetworkSummary,
  SubBlock,
  SubHeader,
  SubTitle,
} from './styles';
import { Props } from './types';

const Community: FC<Props> = ({ connections, network }) => (
  <Container>
    <SectionHeading title="Your community network" />

    {(connections.followers > 0 || connections.newFollowers > 0) && (
      <SubBlock>
        <SubHeader>
          <SubTitle>Your connections</SubTitle>
          <Description>
            Followers and new supporters who engaged with your work.
          </Description>
        </SubHeader>
        <ConnectionsRow>
          <ConnectionBox>
            <ConnectionNumber>{connections.followers}</ConnectionNumber>
            <ConnectionLabel>Followers</ConnectionLabel>
          </ConnectionBox>
          <ConnectionBox>
            <ConnectionNumber>{connections.newFollowers}</ConnectionNumber>
            <ConnectionLabel>New followers</ConnectionLabel>
          </ConnectionBox>
        </ConnectionsRow>
      </SubBlock>
    )}

    {network.length > 0 ? (
      <SubBlock>
        <SubHeader>
          <SubTitle>Inspiration from your network</SubTitle>
          <Description>
            Here&#8217;s what your network has been sharing and contributing.
          </Description>
        </SubHeader>
        <NetworkList>
          {network.map((item) => {
            const fullName = `${item.user.firstName} ${item.user.lastName}`;
            const parts: string[] = [];
            if (item.exercises > 0) {
              parts.push(
                `${item.exercises} ${pluralize(
                  item.exercises,
                  'exercise',
                  'exercises',
                )}`,
              );
            }
            if (item.challengeSubmissions > 0) {
              parts.push(
                `${item.challengeSubmissions} ${pluralize(
                  item.challengeSubmissions,
                  'challenge submission',
                  'challenge submissions',
                )}`,
              );
            }
            if (item.conversations > 0) {
              parts.push(
                `${item.conversations} ${pluralize(
                  item.conversations,
                  'conversation',
                  'conversations',
                )}`,
              );
            }
            if (item.feedbackRequests > 0) {
              parts.push(
                `${item.feedbackRequests} ${pluralize(
                  item.feedbackRequests,
                  'feedback request',
                  'feedback requests',
                )}`,
              );
            }
            const summary = parts.join(' · ');

            return (
              <NetworkItem key={item.user.id}>
                <NetworkLink to={`/members/${item.user.id}`}>
                  <MemberAvatar
                    variant="default"
                    src={item.user.avatar}
                    name={fullName}
                  />
                  <NetworkInfo>
                    <NetworkName>{fullName}</NetworkName>
                    {summary && <NetworkSummary>{summary}</NetworkSummary>}
                  </NetworkInfo>
                </NetworkLink>
              </NetworkItem>
            );
          })}
        </NetworkList>
      </SubBlock>
    ) : (
      <EmptyCard>
        <SubHeader>
          <EmptyTitle>Find Inspiration in the SWD community</EmptyTitle>
          <EmptyBody>
            Check out how other members have been contributing, learning, and
            sharing their work this month.
          </EmptyBody>
        </SubHeader>
        <CommunityButton variant="secondary" mode="small" to="/members">
          explore community members
          <ButtonArrow />
        </CommunityButton>
      </EmptyCard>
    )}
  </Container>
);

export default memo(Community);
